import * as React from "react";
import { Loader2, Printer } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@workspace/ui/components/button";

import { useEposContext } from "../epos-context";
import { printReceiptFailure } from "./print-receipt-error";

/**
 * HA-583 — fires a customer bill/receipt print for an order. The server decides Bill vs Receipt from
 * payment state and returns it, so the success toast reflects what actually printed. Errors (no printer,
 * unreachable, etc.) surface as a toast saying why, with Retry where trying again can help (HA-361). The button disables while in flight (the first line of the
 * AC7 double-tap guard; the server debounce is authoritative).
 */
export function PrintReceiptButton({
	orderId,
	label = "Print Receipt",
	size = "lg",
	variant = "default",
	className,
}: {
	orderId: string;
	label?: string;
	size?: "default" | "sm" | "lg";
	variant?: "default" | "secondary" | "outline";
	className?: string;
}) {
	const { printReceipt } = useEposContext();
	const [isPrinting, setIsPrinting] = React.useState(false);

	const handlePrint = async () => {
		setIsPrinting(true);
		try {
			const result = await printReceipt(orderId);
			if (!result.absorbed) {
				toast.success(
					result.documentType === "Receipt"
						? "Receipt sent to printer"
						: "Bill sent to printer",
				);
			}
		} catch (error) {
			// HA-361 AC4 — nothing was dispatched or recorded, so Retry just asks again.
			const failure = printReceiptFailure(error);
			toast.error(failure.message, {
				action: failure.retryable
					? { label: "Retry", onClick: () => void handlePrint() }
					: undefined,
			});
		} finally {
			setIsPrinting(false);
		}
	};

	return (
		<Button
			size={size}
			variant={variant}
			className={className}
			disabled={isPrinting}
			onClick={handlePrint}
		>
			{isPrinting ? (
				<Loader2 className="mr-2 size-5 animate-spin" />
			) : (
				<Printer className="mr-2 size-5" />
			)}
			{label}
		</Button>
	);
}
